import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  FormControl,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import CardTravelIcon from '@mui/icons-material/CardTravel';
import TimeToLeaveIcon from '@mui/icons-material/TimeToLeave';
import SaveIcon from '@mui/icons-material/Save';
import MadagascarMap from '../../../components/MadagascarMap';
import { useLocalisation } from '../../../hooks/useLocalisation';
import TimeSheetMissionService from '../../../services/TimeSheetMissionService';
import useStyles from '../../../styles/MissionAssignVehicle.style';

const InfoField = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography
      variant="caption"
      sx={{ color: 'text.secondary', textTransform: 'uppercase', fontWeight: 600 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.5 }}>
      {value || '-'}
    </Typography>
  </Grid>
);

const SectionTitle = ({ children }) => (
  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
    {children}
  </Typography>
);

const bullRed = (
  <Box
    component="span"
    sx={{ display: 'inline-block', mx: '5px', transform: 'scale(0.8)', color: 'red', fontSize: '20px' }}
  >
    *
  </Box>
);

const formatDateValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

const normalizeDateInputValue = (value) => {
  if (!value) return '';

  const match = String(value).trim().match(
    /^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s])/
  );
  if (!match) return '';

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year
    || date.getMonth() !== month - 1
    || date.getDate() !== day
  ) {
    return '';
  }

  return `${match[1]}-${match[2]}-${match[3]}`;
};

const getTypeMeta = (type) => {
  if (type === 'DM') {
    return { label: 'Mission', icon: <CardTravelIcon />, color: 'primary' };
  }
  if (type === 'DR') {
    return { label: 'Délai de route', icon: <TimeToLeaveIcon />, color: 'warning' };
  }
  return { label: type || 'Type non renseigné', color: 'default' };
};

const DestinationViewDialog = ({
  open,
  onClose,
  destination,
  setDestinations,
  setMessage,
  missionTimesheetId,
  onSave,
  localOnly = false,
  decompteMode = false,
  transportOptions = [],
  editable = false,
}) => {
  const classes = useStyles();
  const data = destination || {};
  const typeMeta = getTypeMeta(data.type);
  const [loadingBackdrop, setLoadingBackdrop] = useState(false);
  const [geodata, setGeodata] = useState({});
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingDistrictsDepart, setLoadingDistrictsDepart] = useState(false);
  const [loadingDistrictsArrivee, setLoadingDistrictsArrivee] = useState(false);
  const [loadingCommunesDepart, setLoadingCommunesDepart] = useState(false);
  const [loadingCommunesArrivee, setLoadingCommunesArrivee] = useState(false);
  const [formValues, setFormValues] = useState({
    region_depart: null,
    district_depart: null,
    commune_depart: null,
    fokontany_depart: null,
    region_arrivee: null,
    district_arrivee: null,
    commune_arrivee: null,
    fokontany_arrivee: null,
    action: data.mission_timesheet_destination_id ? 'update' : 'add',
    mission_destination_id: data.mission_destination_id || null,
    mission_timesheet_destination_id: data.mission_timesheet_destination_id || null,
    mission_planning_agenda_id : data.mission_planning_agenda_id || null,
    mission_timesheet_id : data.mission_timesheet_id || missionTimesheetId || null,
    date_begin: normalizeDateInputValue(data.date_begin),
    date_end: normalizeDateInputValue(data.date_end),
    lieu_depart: '',
    lieu_arrivee: '',
    heure_begin: data.heure_begin || '',
    heure_end: data.heure_end || '',
    type: data.type || 'DM',
    activite: data.activite || '',
    id_financement_mis: data.id_financement_mis ?? '',
    code_financement_mis: data.code_financement_mis || '',
    transport_id: data.transport_id ?? null,
    transport_type: data.transport_type || '',
    mission_rent_id: data.mission_rent_id ?? null,
    distance_km: data.distance_km ?? '',
    km_depart: data.km_depart ?? '',
    km_arrivee: data.km_arrivee ?? '',
    between_site_start_hour: data.between_site_start_hour || '',
    between_site_end_hour: data.between_site_end_hour || '',
    on_site_arrivee_hour: data.on_site_arrivee_hour || '',
    on_site_depart_hour: data.on_site_depart_hour || '',
  });
  const {
    regionsDepart,
    regionsArrivee,
    districtsDepart,
    districtsArrivee,
    communesDepart,
    communesArrivee,
    fokontanyDepart,
    fokontanyArrivee,
    fetchRegions,
    fetchDistrictsByRegion,
    fetchCommunesByDistrict,
    fetchFokontanyByCommune,
  
  } = useLocalisation();

  const findOption = (list = [], idKey, idValue, nameKey, nameValue) =>
    list.find(
      (item) =>
        (idValue && String(item[idKey]) === String(idValue)) ||
        (nameValue && item[nameKey]?.toLowerCase() === nameValue?.toLowerCase())
    ) || null;

  useEffect(() => {
    if (!open || !destination) return;

    let isSubscribed = true;

    const populateInitialValues = async () => {
      const shouldLoadRegions = !regionsDepart.length || !regionsArrivee.length;
      if (shouldLoadRegions) {
        setLoadingRegions(true);
      }
      const regions = regionsDepart.length ? regionsDepart : await fetchRegions();
      if (!isSubscribed) return;
      if (shouldLoadRegions) {
        setLoadingRegions(false);
      }

      const regionDepartOption = findOption(
        regions,
        'id_region',
        destination.region_depart_id,
        'nom_region',
        destination.region_depart
      );
      const regionArriveeOption = findOption(
        regions,
        'id_region',
        destination.region_arrivee_id,
        'nom_region',
        destination.region_arrivee
      );

      setFormValues((prev) => ({
        ...prev,
        action: destination.mission_timesheet_destination_id ? 'update' : 'add',
        mission_destination_id: destination.mission_destination_id || null,
        mission_timesheet_destination_id: destination.mission_timesheet_destination_id || null,
        mission_planning_agenda_id: destination.mission_planning_agenda_id || null,
        mission_timesheet_id: destination.mission_timesheet_id || missionTimesheetId || null,
        date_begin: normalizeDateInputValue(destination.date_begin),
        date_end: normalizeDateInputValue(destination.date_end),
        region_depart: regionDepartOption,
        region_arrivee: regionArriveeOption,
        lieu_depart: destination.lieu_depart || '',
        lieu_arrivee: destination.lieu_arrivee || '',
        heure_begin: destination.heure_begin || '',
        heure_end: destination.heure_end || '',
        type: destination.type || 'DM',
        activite: destination.activite || '',
        id_financement_mis: destination.id_financement_mis ?? '',
        code_financement_mis: destination.code_financement_mis || '',
        transport_id: destination.transport_id ?? null,
        transport_type: destination.transport_type || '',
        mission_rent_id: destination.mission_rent_id ?? null,
        distance_km: destination.distance_km ?? '',
        km_depart: destination.km_depart ?? '',
        km_arrivee: destination.km_arrivee ?? '',
        between_site_start_hour: destination.between_site_start_hour || '',
        between_site_end_hour: destination.between_site_end_hour || '',
        on_site_arrivee_hour: destination.on_site_arrivee_hour || '',
        on_site_depart_hour: destination.on_site_depart_hour || '',
      }));

      if (regionDepartOption?.id_region) {
        if (!districtsDepart[regionDepartOption.id_region]) {
          setLoadingDistrictsDepart(true);
        }
        const districts = await fetchDistrictsByRegion(regionDepartOption.id_region, 'depart');
        if (!isSubscribed) return;
        setLoadingDistrictsDepart(false);
        const districtDepartOption = findOption(
          districts,
          'id_district',
          destination.district_depart_id,
          'nom_district',
          destination.district_depart
        );
        setFormValues((prev) => ({ ...prev, district_depart: districtDepartOption }));

        if (districtDepartOption?.id_district) {
          if (!communesDepart[districtDepartOption.id_district]) {
            setLoadingCommunesDepart(true);
          }
          const communes = await fetchCommunesByDistrict(districtDepartOption.id_district, 'depart');
          if (!isSubscribed) return;
          setLoadingCommunesDepart(false);
          const communeDepartOption = findOption(
            communes,
            'id_commune',
            destination.commune_depart_id,
            'nom_commune',
            destination.commune_depart
          );
          setFormValues((prev) => ({ ...prev, commune_depart: communeDepartOption }));

          if (communeDepartOption?.id_commune) {
            const fokontanys = await fetchFokontanyByCommune(
              communeDepartOption.id_commune,
              'depart'
            );
            if (!isSubscribed) return;
            const fokontanyDepartOption = findOption(
              fokontanys,
              'id_fokontany',
              destination.fokontany_depart_id,
              'nom_fokontany',
              destination.fokontany_depart
            );
            setFormValues((prev) => ({ ...prev, fokontany_depart: fokontanyDepartOption }));
          }
        }
      }

      if (regionArriveeOption?.id_region) {
        if (!districtsArrivee[regionArriveeOption.id_region]) {
          setLoadingDistrictsArrivee(true);
        }
        const districts = await fetchDistrictsByRegion(regionArriveeOption.id_region, 'arrivee');
        if (!isSubscribed) return;
        setLoadingDistrictsArrivee(false);
        const districtArriveeOption = findOption(
          districts,
          'id_district',
          destination.district_arrivee_id,
          'nom_district',
          destination.district_arrivee
        );
        setFormValues((prev) => ({ ...prev, district_arrivee: districtArriveeOption }));

        if (districtArriveeOption?.id_district) {
          if (!communesArrivee[districtArriveeOption.id_district]) {
            setLoadingCommunesArrivee(true);
          }
          const communes = await fetchCommunesByDistrict(districtArriveeOption.id_district, 'arrivee');
          if (!isSubscribed) return;
          setLoadingCommunesArrivee(false);
          const communeArriveeOption = findOption(
            communes,
            'id_commune',
            destination.commune_arrivee_id,
            'nom_commune',
            destination.commune_arrivee
          );
          setFormValues((prev) => ({ ...prev, commune_arrivee: communeArriveeOption }));

          if (communeArriveeOption?.id_commune) {
            const fokontanys = await fetchFokontanyByCommune(
              communeArriveeOption.id_commune,
              'arrivee'
            );
            if (!isSubscribed) return;
            const fokontanyArriveeOption = findOption(
              fokontanys,
              'id_fokontany',
              destination.fokontany_arrivee_id,
              'nom_fokontany',
              destination.fokontany_arrivee
            );
            setFormValues((prev) => ({ ...prev, fokontany_arrivee: fokontanyArriveeOption }));
          }
        }
      }
    };

    populateInitialValues();

    return () => {
      isSubscribed = false;
    };
  }, [destination, open, missionTimesheetId]);

  useEffect(() => {
    let isSubscribed = true;
    const loadDistricts = async () => {
      const regionId = formValues?.region_depart?.id_region;
      if (!regionId) {
        if (isSubscribed) setLoadingDistrictsDepart(false);
        return;
      }
      if (districtsDepart[regionId]) {
        if (isSubscribed) setLoadingDistrictsDepart(false);
        return;
      }
      setLoadingDistrictsDepart(true);
      await fetchDistrictsByRegion(regionId, 'depart');
      if (isSubscribed) setLoadingDistrictsDepart(false);
    };
    loadDistricts();
    return () => {
      isSubscribed = false;
    };
  }, [formValues.region_depart, districtsDepart]);
  useEffect(() => {
    let isSubscribed = true;
    const loadCommunes = async () => {
      const districtId = formValues.district_depart?.id_district;
      if (!districtId) {
        if (isSubscribed) setLoadingCommunesDepart(false);
        return;
      }
      if (communesDepart[districtId]) {
        if (isSubscribed) setLoadingCommunesDepart(false);
        return;
      }
      setLoadingCommunesDepart(true);
      await fetchCommunesByDistrict(districtId, 'depart');
      if (isSubscribed) setLoadingCommunesDepart(false);
    };
    loadCommunes();
    return () => {
      isSubscribed = false;
    };
  }, [formValues.district_depart, communesDepart]);
  useEffect(() => {
    fetchFokontanyByCommune(formValues.commune_depart?.id_commune, 'depart');
  }, [formValues.commune_depart]);
  useEffect(() => {
    let isSubscribed = true;
    const loadDistricts = async () => {
      const regionId = formValues?.region_arrivee?.id_region;
      if (!regionId) {
        if (isSubscribed) setLoadingDistrictsArrivee(false);
        return;
      }
      if (districtsArrivee[regionId]) {
        if (isSubscribed) setLoadingDistrictsArrivee(false);
        return;
      }
      setLoadingDistrictsArrivee(true);
      await fetchDistrictsByRegion(regionId, 'arrivee');
      if (isSubscribed) setLoadingDistrictsArrivee(false);
    };
    loadDistricts();
    return () => {
      isSubscribed = false;
    };
  }, [formValues.region_arrivee, districtsArrivee]);
  useEffect(() => { 
    let isSubscribed = true;
    const loadCommunes = async () => {
      const districtId = formValues.district_arrivee?.id_district;
      if (!districtId) {
        if (isSubscribed) setLoadingCommunesArrivee(false);
        return;
      }
      if (communesArrivee[districtId]) {
        if (isSubscribed) setLoadingCommunesArrivee(false);
        return;
      }
      setLoadingCommunesArrivee(true);
      await fetchCommunesByDistrict(districtId, 'arrivee');
      if (isSubscribed) setLoadingCommunesArrivee(false);
    };
    loadCommunes();
    return () => {
      isSubscribed = false;
    };
  }, [formValues.district_arrivee, communesArrivee]);
  useEffect(() => {
    fetchFokontanyByCommune(formValues.commune_arrivee?.id_commune, 'arrivee');
  }, [formValues.commune_arrivee]);
 

  const handleChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
     setLoadingBackdrop(true);
        try { 
          const currentTimesheetId = formValues.mission_timesheet_id || missionTimesheetId || null;
          const destinationTimesheetId = formValues.mission_timesheet_destination_id || null;
          const dataToSend = {
            ...destination,
            ...formValues,
            action: destinationTimesheetId ? 'update' : 'add',
            mission_destination_id: formValues.mission_destination_id || null,
            mission_timesheet_destination_id: destinationTimesheetId,
            mission_planning_agenda_id: formValues.mission_planning_agenda_id || null,
            region_depart: formValues.region_depart?.nom_region || null,
            district_depart: formValues.district_depart?.nom_district || null,
            commune_depart: formValues.commune_depart?.nom_commune || null,
            fokontany_depart: formValues.fokontany_depart?.nom_fokontany || null,
            region_arrivee: formValues.region_arrivee?.nom_region || null,
            district_arrivee: formValues.district_arrivee?.nom_district || null,
            commune_arrivee: formValues.commune_arrivee?.nom_commune || null,
            fokontany_arrivee: formValues.fokontany_arrivee?.nom_fokontany || null,
            mission_timesheet_id: currentTimesheetId,
            missionnaries: Array.isArray(destination?.missionnaries)
              ? destination.missionnaries
                  .map((missionary) => missionary.missionary_id || missionary.user_id)
                  .filter(Boolean)
              : [],
          }

          if (localOnly || currentTimesheetId === null || currentTimesheetId === undefined || String(currentTimesheetId) === '0') {
            if (typeof onSave === 'function') {
              onSave(dataToSend);
            }
            return;
          }

          const response = await TimeSheetMissionService.addOrUpdateDestination(dataToSend);
          if (response && response.status) {
            if (typeof setDestinations === 'function') {
              setDestinations((prevDestinations) => {
                const index = destination.index;
                const updatedDestinations = [...prevDestinations];
                updatedDestinations[index] = {
                  ...updatedDestinations[index],
                  ...dataToSend,
                  mission_timesheet_destination_id: response.data.mission_timesheet_destination_id,
                };
                return updatedDestinations;
              });
            }

            onClose();      
          } else if (typeof setMessage === 'function') {
            setMessage({
              open: true,
              color: 'red',
              messageInfo: response?.message || 'Erreur lors de la sauvegarde des modifications.',
            });
          }

        } catch (error) {
          if (typeof setMessage === 'function') {
            setMessage({
              open: true,
              color: 'red',
              messageInfo: error.message || 'Erreur lors de la sauvegarde des modifications.',
            });
          }
          console.error('Erreur lors de la sauvegarde des modifications :', error);
        } finally {
          setLoadingBackdrop(false);
        }

  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={editable ? 'lg' : 'md'}
      PaperProps={{ className: classes.dialogPaperCompact }}
    >
      <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
        {editable ? "Modifier la destination" : "Détails de la destination"}
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        {!destination ? (
          <Typography variant="body2" color="text.secondary">
            Aucune donnée de destination disponible.
          </Typography>
        ) : editable ? (
          <DialogContentText component="div" className={`${classes.dialogText} ${classes.formArea}`}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <label className={classes.labelPosition}>Type</label>
                  {decompteMode ? (
                    <Autocomplete
                      fullWidth
                      size="small"
                      options={[{ value: 'DM', label: 'Mission' }, { value: 'DR', label: 'Délai de route' }]}
                      value={[{ value: 'DM', label: 'Mission' }, { value: 'DR', label: 'Délai de route' }].find((option) => option.value === formValues.type) || null}
                      onChange={(_, option) => handleChange('type', option?.value || '')}
                      renderInput={(params) => <TextField {...params} label="Type" />}
                    />
                  ) : <TextField fullWidth size="small" value={typeMeta.label} InputProps={{ readOnly: true }} />}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <label className={classes.labelPosition}>Date de départ</label>
                  <TextField fullWidth size="small" type="date" value={formValues.date_begin || ''} InputProps={{ readOnly: !decompteMode }} onChange={(e) => handleChange('date_begin', e.target.value)} />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <label className={classes.labelPosition}>Date d'arrivée</label>
                  <TextField fullWidth size="small" type="date" value={formValues.date_end || ''} InputProps={{ readOnly: !decompteMode }} onChange={(e) => handleChange('date_end', e.target.value)} />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <label className={classes.labelPosition}>Heure de départ {bullRed}</label>
                  <TextField fullWidth size="small" type="time" value={formValues.heure_begin || ''} onChange={(e) => handleChange('heure_begin', e.target.value)} InputLabelProps={{ shrink: true }} />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <label className={classes.labelPosition}>Heure d'arrivée {bullRed}</label>
                  <TextField fullWidth size="small" type="time" value={formValues.heure_end || ''} onChange={(e) => handleChange('heure_end', e.target.value)} InputLabelProps={{ shrink: true }} />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <label className={classes.labelPosition}>Lieu de départ {bullRed}</label>
                  {loadingRegions && <LinearProgress />}
                  <Autocomplete fullWidth size="small" options={regionsDepart} getOptionLabel={(option) => option.nom_region || ''} value={formValues.region_depart || null} disabled={loadingRegions} onChange={(_, value) => handleChange('region_depart', value || null)} sx={{ mb: 1 }} renderInput={(params) => <TextField {...params} label="Région" variant="outlined" />} />
                  {loadingDistrictsDepart && <LinearProgress />}
                  <Autocomplete fullWidth size="small" options={districtsDepart[formValues.region_depart?.id_region] || []} getOptionLabel={(option) => option.nom_district || ''} value={formValues.district_depart || null} disabled={loadingDistrictsDepart} onChange={(_, value) => handleChange('district_depart', value || null)} sx={{ mb: 1 }} renderInput={(params) => <TextField {...params} label="District" variant="outlined" />} />
                  {loadingCommunesDepart && <LinearProgress />}
                  <Autocomplete fullWidth size="small" options={communesDepart[formValues.district_depart?.id_district] || []} getOptionLabel={(option) => option.nom_commune || ''} value={formValues.commune_depart || null} disabled={loadingCommunesDepart} onChange={(_, value) => handleChange('commune_depart', value || null)} sx={{ mb: 1 }} renderInput={(params) => <TextField {...params} label="Commune" variant="outlined" />} />
                  <Autocomplete fullWidth size="small" options={fokontanyDepart[formValues.commune_depart?.id_commune] || []} getOptionLabel={(option) => option.nom_fokontany || ''} value={formValues.fokontany_depart || null} onChange={(_, value) => handleChange('fokontany_depart', value || null)} renderInput={(params) => <TextField {...params} label="Fokontany" variant="outlined" />} />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <label className={classes.labelPosition}>Lieu d'arrivée {bullRed}</label>
                  {loadingRegions && <LinearProgress />}
                  <Autocomplete fullWidth size="small" options={regionsArrivee} getOptionLabel={(option) => option.nom_region || ''} value={formValues.region_arrivee || null} disabled={loadingRegions} onChange={(_, value) => handleChange('region_arrivee', value || null)} sx={{ mb: 1 }} renderInput={(params) => <TextField {...params} label="Région" variant="outlined" />} />
                  {loadingDistrictsArrivee && <LinearProgress />}
                  <Autocomplete fullWidth size="small" options={districtsArrivee[formValues.region_arrivee?.id_region] || []} getOptionLabel={(option) => option.nom_district || ''} value={formValues.district_arrivee || null} disabled={loadingDistrictsArrivee} onChange={(_, value) => handleChange('district_arrivee', value || null)} sx={{ mb: 1 }} renderInput={(params) => <TextField {...params} label="District" variant="outlined" />} />
                  {loadingCommunesArrivee && <LinearProgress />}
                  <Autocomplete fullWidth size="small" options={communesArrivee[formValues.district_arrivee?.id_district] || []} getOptionLabel={(option) => option.nom_commune || ''} value={formValues.commune_arrivee || null} disabled={loadingCommunesArrivee} onChange={(_, value) => handleChange('commune_arrivee', value || null)} sx={{ mb: 1 }} renderInput={(params) => <TextField {...params} label="Commune" variant="outlined" />} />
                  <Autocomplete fullWidth size="small" options={fokontanyArrivee[formValues.commune_arrivee?.id_commune] || []} getOptionLabel={(option) => option.nom_fokontany || ''} value={formValues.fokontany_arrivee || null} onChange={(_, value) => handleChange('fokontany_arrivee', value || null)} renderInput={(params) => <TextField {...params} label="Fokontany" variant="outlined" />} />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <label className={classes.labelPosition}>Départ</label>
                  <TextField label="Départ" variant="outlined" fullWidth size="small" value={formValues.lieu_depart || ''} onChange={(e) => handleChange('lieu_depart', e.target.value)} />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <label className={classes.labelPosition}>Vers</label>
                  <TextField label="Vers" variant="outlined" fullWidth size="small" value={formValues.lieu_arrivee || ''} onChange={(e) => handleChange('lieu_arrivee', e.target.value)} />
                </FormControl>
              </Grid>
              {decompteMode && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      fullWidth
                      size="small"
                      options={transportOptions}
                      value={transportOptions.find((option) => String(option.transport_id) === String(formValues.transport_id)) || null}
                      getOptionLabel={(option) => option.vhc_imm || option.rent_type || `Transport ${option.transport_id}`}
                      onChange={(_, option) => setFormValues((previous) => ({
                        ...previous,
                        transport_id: option?.transport_id ?? null,
                        transport_type: option?.vhc_type || option?.rent_type || '',
                        mission_rent_id: option?.mission_rent_id ?? null,
                      }))}
                      renderInput={(params) => <TextField {...params} label="Transport" />}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContentText>
        ) : (
          <Grid container spacing={2} sx={{ marginLeft: 4 }}>
            <Grid container item sm={6} md={12} xl={12} spacing={2} marginTop={1}>
              <Chip icon={typeMeta.icon} label={typeMeta.label} color={typeMeta.color} sx={{ mt: 1 }} />
              <Grid container spacing={2}>
                <InfoField label="Date de début" value={formatDateValue(data.date_begin)} />
                <InfoField label="Date de fin" value={formatDateValue(data.date_end)} />
                <InfoField label="Heure de départ" value={data.heure_begin || ''} />
                <InfoField label="Heure d'arrivée" value={data.heure_end || ''} />
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6} md={6} xl={6}>
                  <SectionTitle>Départ</SectionTitle>
                  <Grid container spacing={2}>
                    <InfoField label="Région" value={data.region_depart || ''} />
                    <InfoField label="District" value={data.district_depart || ''} />
                    <InfoField label="Commune" value={data.commune_depart || ''} />
                    <InfoField label="Fokontany" value={data.fokontany_depart || ''} />
                  </Grid>
                </Grid>
                <Grid item xs={6} md={6} xl={6}>
                  <SectionTitle>Arrivée</SectionTitle>
                  <Grid container spacing={2}>
                    <InfoField label="Région" value={data.region_arrivee || ''} />
                    <InfoField label="District" value={data.district_arrivee || ''} />
                    <InfoField label="Commune" value={data.commune_arrivee || ''} />
                    <InfoField label="Fokontany" value={data.fokontany_arrivee || ''} />
                  </Grid>
                </Grid>
              </Grid>
              {data.observation && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <SectionTitle>Observation</SectionTitle>
                  <Typography variant="body2">{data.observation}</Typography>
                </>
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        {editable ? (
          <>
            <span style={{ color: 'red' }}>{bullRed} indique un champ obligatoire</span>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={onClose} variant="outlined" color="error" className={classes.actionButton}>Annuler</Button>
              <Button onClick={handleSave} variant="contained" color="info" disabled={loadingBackdrop} startIcon={<SaveIcon />} className={classes.actionButton}>Enregistrer</Button>
            </Box>
          </>
        ) : (
          <Button onClick={onClose} variant="outlined" color="primary" className={classes.actionButton}>Fermer</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DestinationViewDialog;
